import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tag, Loader2, Check, X } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { publicApi, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export interface ValidatedDiscount {
  // tenant_discounts.id is a UUID string (the old `number` type made callers
  // parseInt it, mangling the id before it reached the booking API).
  id: string;
  label: string;
  // Raw DB values returned by /discounts/validate. The old
  // "percentage" | "fixed_amount" strings never matched what the API actually
  // sends, so percent discounts were silently mispriced on checkout.
  valueType: "percent" | "amount";
  value: string;
}

interface PromoCodeInputProps {
  onDiscountApplied: (discount: ValidatedDiscount | null) => void;
  disabled?: boolean;
}

interface ValidateResponse {
  valid: boolean;
  discount?: ValidatedDiscount;
  message?: string;
}

export function PromoCodeInput({ onDiscountApplied, disabled }: PromoCodeInputProps) {
  const { t } = useTranslation();
  const { slug } = useTenant();
  const [code, setCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appliedDiscount, setAppliedDiscount] = useState<ValidatedDiscount | null>(null);

  const handleApply = async () => {
    if (!code.trim() || !slug) return;

    setIsValidating(true);
    setError(null);

    try {
      const response = await publicApi.post<ValidateResponse>(
        `/api/public/${slug}/discounts/validate`,
        { code: code.trim() }
      );

      if (response.valid && response.discount) {
        // Validate discount structure
        const discount = response.discount;
        if (!discount.id || !discount.label || !discount.valueType || !discount.value) {
          setError(t("checkout.promoCode.invalid"));
          setAppliedDiscount(null);
          onDiscountApplied(null);
          return;
        }
        setAppliedDiscount(discount);
        onDiscountApplied(discount);
        setError(null);
      } else {
        setError(response.message || t("checkout.promoCode.invalid"));
        setAppliedDiscount(null);
        onDiscountApplied(null);
      }
    } catch (err) {
      // Provide specific error messages based on error type
      if (err instanceof ApiError) {
        setError(err.message);
      } else if (err instanceof TypeError && err.message.includes("fetch")) {
        setError(t("errors.network.message"));
      } else {
        setError(t("checkout.promoCode.invalid"));
      }
      setAppliedDiscount(null);
      onDiscountApplied(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemove = () => {
    setCode("");
    setAppliedDiscount(null);
    setError(null);
    onDiscountApplied(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  // If discount is applied, show success state
  if (appliedDiscount) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">{t("checkout.promoCode.title")}</label>
        <div
          className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg"
          data-testid="promo-applied"
        >
          <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800 truncate">
              {code.toUpperCase()}
            </p>
            <p className="text-xs text-green-600">
              {appliedDiscount.label}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="h-8 w-8 p-0 text-green-600 hover:text-green-800 hover:bg-green-100"
            aria-label={t("checkout.promoCode.remove")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">{t("checkout.promoCode.title")}</label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("checkout.promoCode.placeholder")}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={handleKeyDown}
            disabled={disabled || isValidating}
            className="pl-9 uppercase"
            maxLength={32}
            aria-invalid={!!error}
            aria-describedby={error ? "promo-error" : undefined}
            data-testid="promo-input"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleApply}
          disabled={disabled || isValidating || !code.trim()}
          data-testid="promo-apply"
        >
          {isValidating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            t("checkout.promoCode.apply")
          )}
        </Button>
      </div>
      {error && (
        <p id="promo-error" className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default PromoCodeInput;
